import { useState, useEffect, useRef } from 'react';
import { getCookie } from '../utils/utils';
import { DEV_TESTING_COOKIES } from '../utils/consts';
/**
 * Check 1: Workflow equals 'THD_STORE_LOCATION'
 */
const checkWorkflowIsStoreLocation = (): boolean => {
  if (typeof window === 'undefined') return false;
  const localizer = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;
  const workflow = localizer?.status?.getWorkflow?.();
  const result = workflow === 'THD_STORE_LOCATION';
  // eslint-disable-next-line no-console
  // '[Location Check 1] Workflow check:', { workflow, expected: 'THD_STORE_LOCATION', result }
  return result;
};

/**
 * Check 2: Internal + IP-store + store workflow
 * User is internal AND localization method is 'ip-store' AND workflow includes 'store'
 */
const checkInternalIpStoreWorkflow = (): boolean => {
  if (typeof window === 'undefined') return false;
  const localizer = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;
  const status = localizer?.status;

  const isInternal = status?.isInternal?.() ?? false;
  const localizationMethod = status?.getLocalizationMethod?.() ?? '';
  const workflow = status?.getWorkflow?.() ?? '';

  const result = isInternal && localizationMethod === 'ip-store' && workflow.toLowerCase().includes('store');
  // '[Location Check 2] Internal + IP-store + store workflow:', { isInternal, localizationMethod, workflow, result }
  return result;
};

/**
 * Check 3: isUserInStore() with resolved user store present
 * Status says user is in store AND getUserStore returns a valid store
 */
const checkInStoreWithResolvedStore = (): boolean => {
  if (typeof window === 'undefined') return false;
  const localizer = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;

  const isInStore = localizer?.status?.isUserInStore?.() ?? false;
  const userStore = localizer?.getUserStore?.();

  const result = isInStore && !!userStore;
  // eslint-disable-next-line no-console
  // '[Location Check 4] In-store with resolved store:', { isInStore, userStore, result }
  return result;
};

/**
 * Check 4: Store number present if user is inside store
 * getStoreNumberIfUserIsInsideStore returns a truthy value
 */
const checkStoreNumberPresent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const localizer = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;
  const storeNumber = localizer?.status?.getStoreNumberIfUserIsInsideStore?.();
  const result = !!storeNumber && storeNumber !== 'Not In Store';
  // eslint-disable-next-line no-console
  // '[Location Check 4] Store number present:', { storeNumber, result }
  return result;
};

/**
 * Mobile App User In Store Check
 * Checks if user is in store using mobile app specific logic:
 * (isUserInStore() && getUserStore() is present) OR workflow equals 'THD_STORE_LOCATION'
 */
const checkIsMobileAppUserInStore = (): boolean => {
  if (typeof window === 'undefined') return false;
  const localizer = window?.THD_LOCALIZER_AUTO_INIT?.Localizer;

  const isUserInStore = localizer?.status?.isUserInStore?.() ?? false;
  const userStore = localizer?.getUserStore?.();
  const workflow = localizer?.status?.getWorkflow?.() ?? '';

  const result = (isUserInStore && !!userStore) || workflow === 'THD_STORE_LOCATION';

  // '[Mobile App In-Store Check]:', { isUserInStore, userStore, workflow, result }

  return result;
};

// ============================================================================
// CHECK REGISTRY
// Centralized map of all available checks
// ============================================================================

const CHECK_REGISTRY: Record<string, () => boolean> = {
  workflowIsStoreLocation: checkWorkflowIsStoreLocation,
  internalIpStoreWorkflow: checkInternalIpStoreWorkflow,
  inStoreWithResolvedStore: checkInStoreWithResolvedStore,
  storeNumberPresent: checkStoreNumberPresent,
  isMobileAppUserInStore: checkIsMobileAppUserInStore,
};

// ============================================================================
// CONFIGURATION
// Toggle which checks to run by adding/removing keys from this array
// ============================================================================

const ENABLED_CHECKS: string[] = [
  'workflowIsStoreLocation',
  'internalIpStoreWorkflow',
  'inStoreWithResolvedStore',
  'storeNumberPresent',
  'isMobileAppUserInStore'
];

// ============================================================================
// EVALUATION FUNCTION
// ============================================================================

/**
 * Evaluates enabled checks and returns whether user is in store
 * @param customEnabledChecks - Optional array of check keys to run (defaults to ENABLED_CHECKS)
 * @param aggregator - How to combine results: 'any' (OR) or 'all' (AND). Default is 'any'
 * @returns boolean indicating if user is in store
 */
export const evaluateInStore = (
  customEnabledChecks: string[] = ENABLED_CHECKS,
  aggregator: 'any' | 'all' = 'any'
): boolean => {
  const checks = customEnabledChecks
    .map((key) => CHECK_REGISTRY[key])
    .filter(Boolean);

  if (checks.length === 0) return false;

  if (aggregator === 'all') {
    return checks.every((check) => check());
  }

  // Default: 'any' (OR)
  return checks.some((check) => check());
};

// ============================================================================
// EXPORTED HOOK
// ============================================================================

/**
 * Custom hook to manage user location status using THD Localizer
 * Checks if user is in store on component mount
 */
export const useLocationStatus = () => {
  const [isUserInStore, setIsUserInStore] = useState(false);
  const isCheckingLocationRef = useRef(false);

  const scanToPayInStoreCookie = getCookie(DEV_TESTING_COOKIES.SCAN_TO_PAY_IN_STORE);

  /**
   * Main function to check user location status
   * Uses the modular predicate system
   */
  const checkUserLocationStatus = (): void => {

    if (scanToPayInStoreCookie === true) {
      setIsUserInStore(true);
      return;
    }
    if (scanToPayInStoreCookie === false) {
      setIsUserInStore(false);
      return;
    }
    // Prevent overlapping calls
    if (isCheckingLocationRef.current) {
      return;
    }

    isCheckingLocationRef.current = true;

    try {
      const isInStore = evaluateInStore();
      setIsUserInStore(isInStore);
    } finally {
      isCheckingLocationRef.current = false;
    }
  };

  /**
   * Set up the location checking on initial render only
   */
  useEffect(() => {
    checkUserLocationStatus();
  }, []);

  return {
    isUserInStore,
    isCheckingLocation: isCheckingLocationRef.current,
    checkUserLocationStatus,
  };
};
